/**
 * Envoltorio de tiempo límite para consultas al backend.
 *
 * Motivo: si la red del comercio se degrada (wifi saturado, hotspot caído),
 * una consulta puede quedar pendiente indefinidamente. Sin límite, la pantalla
 * se queda cargando para siempre y el cajero no puede reintentar.
 *
 * Importante: NO cancela la operación en el servidor, solo libera la interfaz
 * para que el usuario reciba un mensaje claro y pueda volver a intentar. Por eso
 * NUNCA se usa para escrituras críticas (ventas), solo para lecturas.
 */

export const DEFAULT_QUERY_TIMEOUT_MS = 20000;

export class QueryTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'QueryTimeoutError';
  }
}

export async function withTimeout<T>(
  operation: PromiseLike<T>,
  ms: number = DEFAULT_QUERY_TIMEOUT_MS,
  message = 'El servidor tardó demasiado en responder. Revisa tu conexión e intenta de nuevo.'
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new QueryTimeoutError(message)), ms);
  });

  try {
    return await Promise.race<T>([Promise.resolve(operation), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Divide una lista en bloques para evitar URLs/consultas gigantes. */
export function chunk<T>(items: T[], size = 100): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}